import React, { useEffect, useState } from "react";
import { ApiConfig } from "../../../api/apiConfig/ApiConfig";
import AuthService from "../../../api/services/AuthService";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponent/CustomAlertMessage";
import { CSVLink } from "react-csv";
import LoaderHelper from "../../../customComponent/Loading/LoaderHelper";
import moment from "moment";
import DataTableBase from "../../../customComponent/DataTable";

const SIPRequest = () => {
    const [fundsDeposit, setFundsDeposit] = useState([]);
    const [allData, setAllData] = useState([]);
    // Investment modal states
    const [showInvestmentModal, setShowInvestmentModal] = useState(false);
    const [selectedAffiliate, setSelectedAffiliate] = useState(null);
    const [investmentData, setInvestmentData] = useState({ amount: '', self_roi_percent: '', uplines: [] });
    const [currency, setCurrency] = useState("INR");
    const [walletBalance, setWalletBalance] = useState(0);
    const [packageDetails, setPackageDetails] = useState(null);
    const [packageList, setPackageList] = useState([]);

    function statusFormatter(row) {
        return <>
            <button className={`btn btn-success btn-sm   me-2 my-1`} onClick={() => { openInvestmentModal(row?.investor, row?._id, row) }}>Approve
            </button>
            <button className={`btn btn-danger btn-sm   me-2 my-1`} onClick={() => { updateDepositRequest(row?._id, "REJECTED") }}>Reject
            </button>
        </>
    };

    const userIdFollow = (row) => {
        return (
            row.investor._id
        );
    };

    const handleImageShow = (row) => {
        return (
            row?.installmentschedules?.transaction_slip ? <a href={ApiConfig?.appUrl + row?.installmentschedules?.transaction_slip} target="_blank" rel="noreferrer">  <img style={{ width: "40%", height: "auto" }} src={ApiConfig?.appUrl + row?.installmentschedules?.transaction_slip} alt="Receipt" className="table-img cursor_pointer" /></a> : "---"
        );
    };



    const columns = [
        { name: "Date", selector: row => moment(row?.investmentDate).format("MMM Do YYYY"), wrap: true },
        { name: "User ID", wrap: true, selector: userIdFollow },
        { name: "Email", wrap: true, selector: row => row.investor.emailId, },
        { name: "SIP Plan", sortable: true, wrap: true, selector: row => `${row.planDetails?.duration_days} ${(row.planDetails?.duration_days == 1)?'Year':"Years"}`, },
        { name: "Amount", sortable: true, wrap: true, selector: row => `${row.amount}`, },
        { name: "Transaction No.", sortable: true, wrap: true, selector: row => row?.installmentschedules?.transaction_number || "---" },
        { name: "Receipt", sortable: true, wrap: true, selector: handleImageShow, },
        { name: "Status", wrap: true, selector: statusFormatter, },
    ];

    useEffect(() => {
        handleFundDeposit();
        fetchAllPackageList();
    }, []);

    const handleFundDeposit = async () => {
        LoaderHelper.loaderStatus(true);
        await AuthService.getSIPRequests("INACTIVE","SIP").then(async result => {
            LoaderHelper.loaderStatus(false);
            if (result.success) {
                try {
                    setFundsDeposit(result.data);
                    setAllData(result.data);
                } catch (error) {
                    alertErrorMessage(error);
                }
            } else {
                LoaderHelper.loaderStatus(false);
            }
        });
    };

    const updateDepositRequest = async (id, status) => {
        LoaderHelper.loaderStatus(true);
        await AuthService.rejectSIPRequest(id, status).then(async result => {
            LoaderHelper.loaderStatus(false);
            if (result.success) {
                try {
                    alertSuccessMessage(result?.message)
                    handleFundDeposit()
                } catch (error) {
                    alertErrorMessage(error);
                }
            } else {
                alertErrorMessage(result?.message)
                LoaderHelper.loaderStatus(false);
            }
        });
    };

    const openInvestmentModal = async (user, sipRequestId, sipData) => {
        setSelectedAffiliate(user);
        getWalletBalance(user?._id);
        
        // Pre-fill investment data from SIP request
        const preFilledData = {
            sipRequestId,
            amount: sipData?.amount || '',
            self_roi_percent: sipData?.planDetails?.return_percentage_monthly || '',
            investmentDate: sipData?.investmentDate ? new Date(sipData.investmentDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
            remarks: `SIP Investment from request #${sipData?.installmentschedules?.transaction_number || ''}`,
            uplines: []
        };
        
        // Find matching package
        const matchingPackage = packageList.find(pkg => pkg._id === sipData?.plan_id);
        if (matchingPackage) {
            setPackageDetails(matchingPackage);
            if (matchingPackage.type === "SIP") {
                preFilledData.self_roi_percent = (matchingPackage?.return_percentage_monthly / 12);
            } else {
                preFilledData.self_roi_percent = matchingPackage?.return_percentage_monthly;
            }
        }

        try {
            const response = await AuthService.getUplines(user?._id);
            if (response.success) {
                preFilledData.uplines = response.uplines?.map(l => ({ ...l, roi_percent: '' })) || [];
                setInvestmentData(preFilledData);
                setShowInvestmentModal(true);
            } else {
                alertErrorMessage("Unable to fetch uplines");
            }
        } catch (e) {
            alertErrorMessage("Failed to load uplines");
        }
    };

    const handleInvestmentSubmit = async (sipRequestId) => {
        try {
            if (!packageDetails?._id) {
                alertErrorMessage("Please select a package");
                return;
            }
            if (!investmentData.amount || !investmentData.self_roi_percent || !investmentData.investmentDate) {
                alertErrorMessage("Please fill all required fields");
                return;
            }
            
            LoaderHelper.loaderStatus(true);
            const payload = {
                investmentId: investmentData.sipRequestId,
                amount: parseFloat(investmentData.amount),
                currency: currency,
                self_roi_percent: parseFloat(investmentData.self_roi_percent),
                roi_distribution: investmentData.uplines.map(up => ({
                    level: up.level,
                    userId: up.user._id,
                    percent: parseFloat(up.roi_percent) || 0
                })),
                remarks: investmentData.remarks || '',
                investmentDate: investmentData.investmentDate || new Date().toISOString().split('T')[0],
                planId: packageDetails?._id || '',
                type: packageDetails?.type || ''
            };

            const response = await AuthService.approveAffiliateInvestment(investmentData.sipRequestId, payload);
            if (response.success) {
                // Update SIP request status to SUCCESS
                await handleFundDeposit();
                alertSuccessMessage("Investment added and SIP request approved successfully");
                setShowInvestmentModal(false);
                setPackageDetails(null);
            } else {
                alertErrorMessage(response.message);
            }
        } catch {
            alertErrorMessage("Failed to add investment");
        } finally {
            LoaderHelper.loaderStatus(false);
        }
    };

    const fetchAllPackageList = async () => {
        try {
            const result = await AuthService.allPackageList();
            if (result.success) {
                setPackageList(result?.data[0] || []);
            }
        } catch {
            alertErrorMessage("Failed to fetch packages");
        }
    };

    const getWalletBalance = async (userId) => {
        try {
            LoaderHelper.loaderStatus(true);
            const result = await AuthService.getWalletBalance(currency, userId);
            if (result?.success) {
                setWalletBalance(result?.data?.balance || 0);
            }
        } catch (error) {
        } finally { 
            LoaderHelper.loaderStatus(false); 
        }
    };

    function handleSearch(e) {
        const keysToSearch = ["chain", "currency", "emailId", "firstName", "from_address", "to_address", "transaction_hash", "amount", "mobileNumber", "user_id"];
        const searchTerm = e.target.value?.toLowerCase();
        const matchingObjects = allData.filter(obj => { return keysToSearch.some(key => obj[key]?.toString()?.toLowerCase()?.includes(searchTerm)) });
        setFundsDeposit(matchingObjects);
    };

    return (
        <div id="layoutSidenav_content">
            <main>
                <header className="page-header page-header-dark bg-gradient-primary-to-secondary pb-10">
                    <div className="container-xl px-4">
                        <div className="page-header-content pt-4">
                            <div className="row align-items-center justify-content-between">
                                <div className="col-auto mt-4">
                                    <h1 className="page-header-title">
                                        <div className="page-header-icon"><i className="fa fa-dollar-sign"></i></div>
                                        New SIP Request
                                    </h1>
                                </div>
                            </div>
                        </div>
                    </div>
                </header>
                <div className="container-xl px-4 mt-n10">
                    <div className="card mb-4">
                        <div className="card-header">
                            New SIP Request

                            <div className="col-5">
                                <input className="form-control form-control-solid" id="inputLastName" type="text" placeholder="Search here..." name="search" onChange={handleSearch} />
                            </div>
                            {fundsDeposit.length === 0 ? "" :
                                <div className="dropdown">
                                    <button className="btn btn-dark btn-sm dropdown-toggle" id="dropdownFadeInUp" type="button" data-bs-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                                        Export{" "}
                                    </button>
                                    <div className="dropdown-menu animated--fade-in-up" aria-labelledby="dropdownFadeInUp">
                                        <CSVLink data={fundsDeposit} className="dropdown-item">Export as CSV</CSVLink>
                                    </div>
                                </div>}
                        </div>
                        <div className="table-responsive mt-4" width="100%">
                            <DataTableBase columns={columns} data={fundsDeposit} />
                        </div>
                    </div>
                </div>

                {/* Investment Modal */}
                {showInvestmentModal && (
                    <div className="modal lg fade show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
                        <div className="modal-dialog modal-xl">
                            <div className="modal-content px-4">
                                <div className="modal-header">
                                    <h5 className="modal-title">Add Investment for {selectedAffiliate.firstName} {selectedAffiliate.lastName}</h5>
                                    <button className="btn-close" onClick={() => setShowInvestmentModal(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <div className="d-flex gap-2">
                                        <div style={{ width: '65%' }}>
                                            <div className="d-flex gap-2 mt-2">
                                                <div className="form-group mb-2 w-25">
                                                    <label>Currency</label>
                                                    <select className="form-select form-control shadow-soft-inner" onChange={(e) => {
                                                        setPackageDetails(null);
                                                        setCurrency(e.target.value);
                                                    }} value={currency}>
                                                        <option style={{ color: 'gray' }} value="INR">INR </option>
                                                        <option style={{ color: 'gray' }} value="USD">USD </option>
                                                        <option style={{ color: 'gray' }} value="AED">AED </option>
                                                        <option style={{ color: 'gray' }} value="EUR">EUR </option>
                                                    </select>
                                                </div>
                                                <div className="form-group mb-2 w-75">
                                                    <label>Package</label>
                                                    <select disabled={packageList?.length === 0 || currency != "INR"} className="form-select form-control shadow-soft-inner" onChange={(e) => {
                                                        const selectedPackage = packageList.find(item => item.plan_id === e.target.value);
                                                        if (selectedPackage.type === "SIP") {
                                                            setInvestmentData({ ...investmentData, self_roi_percent: (selectedPackage?.return_percentage_monthly / 12) });
                                                            setPackageDetails(selectedPackage);
                                                        } else {
                                                            setInvestmentData({ ...investmentData, self_roi_percent: selectedPackage?.return_percentage_monthly });
                                                            setPackageDetails(selectedPackage);
                                                        }
                                                    }} value={packageDetails?.plan_id || ''}>
                                                        <option value="" style={{ color: 'gray' }}>Select Package </option>
                                                        {packageList?.map((item) => {
                                                            let duration = item?.type == "FIP" ? (item?.duration_days / 365) : item?.duration_days;
                                                            return (
                                                                <option key={item.plan_id} style={{ color: 'gray' }} value={item.plan_id}>{`${item?.type} ${item?.type == "SIP" ? "" : `|| Amount: ${item?.min_amount} - ${item?.max_amount} ${item?.currency}`} || Duration : ${duration} ${duration > 1 ? "Years" : "Year"}`}</option>
                                                            )
                                                        })}
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="d-flex gap-2 my-2">
                                                <div className="form-group w-50">
                                                    <label>Investment Amount ({currency})</label>
                                                    <input type="number" className="form-control" value={investmentData.amount} onWheel={(e) => e.target.blur()} onChange={e => setInvestmentData({ ...investmentData, amount: e.target.value })} />
                                                </div>
                                                <div className="form-group w-50">
                                                    <label>Investment Date </label>
                                                    <input type="date" className="form-control" value={investmentData.investmentDate} onChange={e => setInvestmentData({ ...investmentData, investmentDate: e.target.value })} />
                                                </div>
                                            </div>
                                            <div className="form-group mb-2">
                                                <label>Self ROI %</label>
                                                <input type="number" className="form-control" value={investmentData.self_roi_percent} onWheel={(e) => e.target.blur()} onChange={e => setInvestmentData({ ...investmentData, self_roi_percent: e.target.value })} />
                                            </div>
                                            {investmentData.uplines.map((up, i) => (
                                                <div className="form-group mb-2" key={i}>
                                                    <label>Level {up.level} - {up.user.firstName} {up.user.lastName} ({up.user.emailId})</label>
                                                    <input type="number" className="form-control" onWheel={(e) => e.target.blur()} value={up.roi_percent} onChange={e => {
                                                        const updated = [...investmentData.uplines];
                                                        updated[i].roi_percent = e.target.value;
                                                        setInvestmentData({ ...investmentData, uplines: updated });
                                                    }} />
                                                </div>
                                            ))}
                                            <div className="form-group mb-2">
                                                <label>Remarks</label>
                                                <input type="text" className="form-control" value={investmentData.remarks} onChange={e => setInvestmentData({ ...investmentData, remarks: e.target.value })} />
                                            </div>
                                        </div>
                                        <div style={{ width: '35%' }}>
                                            <h3 className="text-center">Wallet Balance :{walletBalance} {currency} </h3>
                                            <table className="table">
                                                <tbody>
                                                    <tr>
                                                        <td>Plan Type: </td>
                                                        <td className="right_t price_tb">{packageDetails?.type || ''}</td>
                                                    </tr>
                                                    <tr>
                                                        <td>Duration: </td>
                                                        <td className="right_t price_tb">
                                                            {packageDetails?.type == "SIP" ?
                                                                <span>{(packageDetails?.duration_days) || 0} {(packageDetails?.duration_days) > 1 ? "Years" : "Year"}</span> :
                                                                <span>{(packageDetails?.duration_days) / 365 || 0} {(packageDetails?.duration_days) / 365 > 1 ? "Years" : "Year"}</span>
                                                            }
                                                        </td>
                                                    </tr>
                                                    <tr>
                                                        <td className='padding-0'><i className="ri-arrow-right-line"></i> Monthly ROI (%):</td>
                                                        <td className="right_t price_tb padding-0">{investmentData.self_roi_percent || 0} %</td>
                                                    </tr>
                                                    <tr>
                                                        <td className='padding-0'><i className="ri-arrow-right-line"></i> {packageDetails?.type == "FIP" ? "Yearly ROI (%)" : "Overall Growth "}:</td>
                                                        <td className="right_t price_tb padding-0">{packageDetails?.return_percentage_yearly || 0} %</td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    <div className="modal-footer">
                                        <button className="btn btn-secondary" onClick={() => { setPackageDetails(null); setShowInvestmentModal(false) }}>Cancel</button>
                                        <button className="btn btn-primary" onClick={() => handleInvestmentSubmit(fundsDeposit.find(item => item.investor._id === selectedAffiliate._id)?._id)}>Submit & Approve</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    )
}
export default SIPRequest;